import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { TuiBadge, TuiTabs, TuiTextarea, TuiSkeleton } from '@taiga-ui/kit';
import {
  FileDownloadService,
  IsLoadingService,
  XAlertService,
} from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import { WorkflowService } from '@core/services/workflow/workflow.service';
import { BehaviorSubject, debounceTime, finalize, Subject } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { IconButtonComponent } from '@common/components';
import {
  FileExtensionPipe,
  FilePreviewUrlPipe,
  RequestReleaseTimeParserPipe,
} from '@core/pipes';
import {
  TuiDialogContext,
  TuiExpand,
  TuiIcon,
  TuiButton,
  TuiHint,
  TuiGroup,
  TuiInput,
  TuiTextfield,
  TuiLabel,
  TuiDialogService,
} from '@taiga-ui/core';
import {
  Counterparty,
  RequestComment,
  RequestDealDetailsExtended,
  RequestDevice,
  RequestFile,
  RequestFileCategory,
  RequestsListItemTranslations,
  RequestsListItemTranslationsPayload,
  TextCopierTranslations,
  TextCopierTranslationsPayload,
} from '@core/types';
import {
  counterpartyIconResolver,
  isImageByFilename,
  Nullable,
} from '@core/helpers';
import { ClipboardService } from 'ngx-clipboard';
import { RequestModel } from '@core/models';
import { FilePreviewComponent } from '@core/components';
import { RequestFormActualAddressComponent } from '../../../request-form-actual-address';
import { FileGroup } from '@features/requests/request-form/components/request-form-summary/types';
import {
  RequestFormCommentsComponent,
  RequestFormDevicesComponent,
  RequestFormGoodsComponent,
  WorkflowDebugComponent,
  WorkflowSubmissionsInfoComponent,
} from '@features/requests/request-form/components';
import { RequestFormDealDetailsComponent } from '@features/requests/request-form/components/request-form-deal-details/request-form-deal-details.component';
import { RequestFormObdComponent } from '@features/requests/request-form/components/request-form-obd';
import { RequestFormInspectionComponent } from '@features/requests/request-form/components/request-form-inspection';
import { RequestFormQcComponent } from '@features/requests/request-form/components/request-form-qc';
import { GoodsBlockComponent } from '../goods-block';
import { FilesBlockComponent } from '../files-block';
import { ChangeHistoryComponent } from '../change-history';
import { RequestInstallerAssignmentDialogComponent } from '../request-installer-assignment-dialog/request-installer-assignment-dialog.component';

import { environment } from '@environment/environment';
import { ImagePreviewComponent } from '@core/components/image-preview/image-preview.component';
import { DeviceInstallQualityCheckStatus } from '@core/types/DeviceInstallQuality';
import { RouterLink } from '@angular/router';
import {
  RequestFormFilesRoutePaths,
  RequestFormFilesTabTitlesTranslateKeys,
} from '@features/requests/request-form/components/request-form-files/types';
import {
  PolymorpheusComponent,
  PolymorpheusContent,
} from '@taiga-ui/polymorpheus';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  HistoryLogTabs,
  HistoryLogTabsTitlesTranslateKeys,
} from '@core/types/RequestLog';
import { FormsModule } from '@angular/forms';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { RequestService } from '@core/services/request/request.service';
import { RequestFormReleaseCityComponent } from '@features/requests/request-form/components/request-form-release-city/request-form-release-city.component';
import { FeatureFlagsService } from '@core/services/feature-flags.service';
import { RequestWorkflowEventsDialogComponent } from '@features/requests/request-form/components/request-form-workflows/request-workflow-events-dialog/request-workflow-events-dialog.component';

@Component({
  selector: 'x-request-form-summary-desktop',
  imports: [
    TuiSkeleton,
    TuiTextfield,
    TuiLabel,
    CommonModule,
    TranslatePipe,
    IconButtonComponent,
    RequestReleaseTimeParserPipe,
    NgOptimizedImage,
    TuiButton,
    TuiBadge,
    TuiIcon,
    RequestFormActualAddressComponent,
    TuiTextarea,
    RequestFormCommentsComponent,
    ImagePreviewComponent,
    GoodsBlockComponent,
    RequestFormGoodsComponent,
    RouterLink,
    RequestFormDevicesComponent,
    TuiExpand,
    RequestFormObdComponent,
    RequestFormDealDetailsComponent,
    RequestFormInspectionComponent,
    FilesBlockComponent,
    TuiTabs,
    RequestFormQcComponent,
    ChangeHistoryComponent,
    TuiInput,
    FormsModule,
    TuiHint,
    TuiGroup,
    FileExtensionPipe,
    FilePreviewComponent,
    FilePreviewUrlPipe,
    RequestFormReleaseCityComponent,
  ],
  templateUrl: './request-form-summary-desktop.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormSummaryDesktopComponent implements OnInit {
  $request = input.required<RequestModel>({ alias: 'request' });
  $isInitialLoad = input.required<boolean>({ alias: 'isInitialLoad' });
  $formatDateWithLocale = input.required<
    (stringDate?: string | null) => string
  >({
    alias: 'formatDateWithLocale',
  });
  $getCheckList = input.required<() => void>({ alias: 'getCheckList' });
  $shareRequestLink = input.required<() => void>({ alias: 'shareRequestLink' });

  $openYaMaps = input.required<() => void>({ alias: 'openYaMaps' });

  $mainFileGroups = input.required<FileGroup[]>({
    alias: 'mainFileGroups',
  });

  $requestComments = input.required<Nullable<RequestComment[]>>({
    alias: 'requestComments',
  });

  $files = input.required<Nullable<RequestFile[]>>({
    alias: 'files',
  });

  $fileCats = input.required<Nullable<RequestFileCategory[]>>({
    alias: 'fileCats',
  });

  $dealDetails = input.required<Nullable<RequestDealDetailsExtended>>({
    alias: 'dealDetails',
  });

  $requestDevices = input.required<Nullable<RequestDevice[]>>({
    alias: 'requestDevices',
  });

  $devicesInstallQuality = input.required<
    Nullable<
      Record<RequestDevice['deviceHash'], DeviceInstallQualityCheckStatus>
    >
  >({ alias: 'devicesInstallQuality' });

  $workflowUrl = input.required<string>({
    alias: 'workflowUrl',
  });

  $workflowUrlButtonText = input.required<string>({
    alias: 'workflowUrlButtonText',
  });

  $workflowDataExists = input.required<boolean>({
    alias: 'workflowDataExists',
  });

  $showPreviewImage = output<[RequestFile, FileGroup, index: number]>();
  $completeAllProducts = output<void>();
  $toggleAddDeviceForm = output<void>();
  $fileTabChange = output<RequestFormFilesRoutePaths>();
  $showFileModal = output<void>();
  $showRequestHistoryLogs = output<boolean>();
  $historyLogsTabChange = output<HistoryLogTabs>();
  $commentsSelectModeEnabled = output<boolean>();
  $historyLogsSearchChanged = output<string>();

  public lPPhoto: Nullable<RequestFile> = null;
  public ePTS: Nullable<RequestFile> = null;
  public appKDkp: Nullable<RequestFile> = null;
  public appKDl: Nullable<RequestFile> = null;

  public $requestHistoryLogsCounter = signal<number>(0);

  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  private readonly clipboardService = inject(ClipboardService);
  private readonly requestFilesService = inject(RequestFilesService);
  private readonly xAlertService = inject(XAlertService);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly requestService = inject(RequestService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly renderer = inject(Renderer2);
  public readonly permissionService = inject(OutkeepPermissionService);
  private readonly featureFlags = inject(FeatureFlagsService);
  private readonly fileDownloadService = inject(FileDownloadService);
  public readonly workflowService = inject(WorkflowService);

  public fileTabs = Object.values(RequestFormFilesRoutePaths);
  public fileTabIndex = 0;

  public dealDetailsExist = false;
  public readonly $isOpeningEpts = signal(false);

  public showRequestHistoryBlock = false;
  public historyTabs = Object.values(HistoryLogTabs);
  public historyTabIndex = 0;
  public historySearch = '';
  private historySearchSubject = new Subject<string>();

  private mainRequestFiles: {
    [fileRenamePrefix: RequestFileCategory['fileRenamePrefix']]: RequestFile[];
  } = {};

  @ViewChild('goods') goodsContainer!: ElementRef;
  @ViewChild('files') filesContainer!: ElementRef;

  private translates: TextCopierTranslationsPayload &
    Partial<RequestsListItemTranslationsPayload> = {
    [TextCopierTranslations.Copied]: 'Скопировано',
    [TextCopierTranslations.NotFoundToCopy]: 'Нечего копировать',
    [RequestsListItemTranslations.DocNotFound]: 'Документ не найден',
  };

  private readonly $hasViewRequestWorkflowsPermission = toSignal(
    this.permissionService.canViewRequestWorkflows$,
    { initialValue: false },
  );
  private readonly $hasManageRequestWorkflowsPermission = toSignal(
    this.permissionService.canManageRequestWorkflows$,
    { initialValue: false },
  );
  protected readonly $canManageRequestWorkflows = computed(
    () =>
      this.featureFlags.workflowsEnabled &&
      this.$hasManageRequestWorkflowsPermission(),
  );

  readonly $canShowWorkflowsBlock = computed(
    () =>
      this.featureFlags.workflowsEnabled &&
      this.$hasViewRequestWorkflowsPermission() &&
      this.$workflowDataExists(),
  );

  constructor() {
    effect(() => {
      this.dealDetailsExist = !!this.$dealDetails()?.details;
    });
  }

  ngOnInit(): void {
    this.historySearchSubject
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(search => this.$historyLogsSearchChanged.emit(search));

    this.outkeepTranslateService
      .translate([
        ...Object.values(TextCopierTranslations),
        RequestsListItemTranslations.DocNotFound,
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (
          translates: TextCopierTranslationsPayload &
            Partial<RequestsListItemTranslationsPayload>,
        ) => (this.translates = translates),
      );

    if (
      this.requestFilesService.loadedRequestsMainFiles()[this.$request().id]
    ) {
      this.mainRequestFiles =
        this.requestFilesService.loadedRequestsMainFiles()[this.$request().id]!;
      this.prepareMainFiles();
    } else {
      this.loadMainFiles();
    }

    this.requestFilesService.requestsMainFilesWereUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadMainFiles();
      });
  }

  private loadMainFiles() {
    this.requestFilesService
      .loadRequestsMainFile$([this.$request().id])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(files => {
        this.mainRequestFiles = files[this.$request().id];

        this.requestFilesService.setLoadedRequestsMainFiles({
          ...this.requestFilesService.loadedRequestsMainFiles(),
          [this.$request().id]: this.mainRequestFiles,
        });

        this.prepareMainFiles();
      });
  }

  private prepareMainFiles() {
    if (this.mainRequestFiles) {
      this.appKDkp =
        this.mainRequestFiles['obrz_app_k_dkp']?.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )?.[0] ?? null;
      this.appKDl =
        this.mainRequestFiles['obrz_app_k_dl']?.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )?.[0] ?? null;
      this.lPPhoto =
        this.mainRequestFiles['photo_pl']?.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )?.[0] ?? null;
      this.ePTS =
        this.mainRequestFiles['obrz_pts_psm']?.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )?.[0] ?? null;
    }
  }

  @pure
  public requestIconResolver(cpiD: Counterparty['id']) {
    return counterpartyIconResolver(cpiD);
  }

  public copyToClipboard(text: string): void {
    this.clipboardService.copy(text);
    if (!text) {
      this.xAlertService.showMessage(
        this.translates[TextCopierTranslations.NotFoundToCopy],
        {
          appearance: 'warning',
          autoClose: 2000,
        },
      );

      return;
    } else {
      this.xAlertService.showMessage(
        this.translates[TextCopierTranslations.Copied],
        {
          appearance: 'positive',
          autoClose: 2000,
        },
      );
    }
  }

  public openLastCatFile(group: FileGroup) {
    if (!group.filesExists) {
      return;
    }
    this.openFile(group.files.at(-1)!);
  }

  public canCompleteSomeProducts = computed(() => {
    return (
      this.$request().products &&
      this.$request().products!.length > 1 &&
      this.$request().products!.some(product => !product.completed)
    );
  });

  public tryToCompleteAllProducts() {
    this.$completeAllProducts.emit();
  }

  public toggleAddDeviceForm() {
    this.$toggleAddDeviceForm.emit();
  }

  public scrollToGoods() {
    this.goodsContainer?.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  public scrollToFiles() {
    this.filesContainer?.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  protected readonly RequestFormFilesTabTitlesTranslateKeys =
    RequestFormFilesTabTitlesTranslateKeys;

  public onFileTabClick(tab: string) {
    this.$fileTabChange.emit(tab as RequestFormFilesRoutePaths);
  }

  public showDealDetailsModal(content: PolymorpheusContent<TuiDialogContext>) {
    this.dialogService$
      .open(content)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public openInstallerAssignmentModal(): void {
    this.dialogService$
      .open<boolean>(
        new PolymorpheusComponent(RequestInstallerAssignmentDialogComponent),
        {
          size: null,
          closable: true,
          dismissible: true,
          data: {
            request: this.$request(),
          },
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(updated => {
        if (updated) {
          this.requestService.reloadRequest();
        }
      });
  }

  public showFileModal() {
    this.$showFileModal.emit();
  }

  protected readonly HistoryLogTabsTitlesTranslateKeys =
    HistoryLogTabsTitlesTranslateKeys;

  public toggleRequestHistoryBlock() {
    this.showRequestHistoryBlock = !this.showRequestHistoryBlock;
    if (this.showRequestHistoryBlock) {
      this.$showRequestHistoryLogs.emit(this.showRequestHistoryBlock);
    }
  }

  public onHistoryTabClick(tab: string) {
    this.$historyLogsTabChange.emit(tab as HistoryLogTabs);
  }

  public onHistorySearchChange() {
    this.historySearchSubject.next(this.historySearch);
  }

  onRequestHistoryLogsCounterOutput(value: number) {
    this.$requestHistoryLogsCounter.set(value);
  }

  doesCurrentRequestHasDevicesInProducts(): boolean {
    return (this.$request().products || []).some(requestProduct => {
      const lowercasedProductName =
        requestProduct.requestProductId.name.toLowerCase();

      return (
        lowercasedProductName.includes('x-keeper invis duos') ||
        lowercasedProductName.includes('установка оборудования') ||
        lowercasedProductName.includes('installation of equipment') ||
        lowercasedProductName.includes('ekipman kurulumu') ||
        lowercasedProductName.includes('x-keeper obd') ||
        lowercasedProductName.includes('x-keeper online slim') ||
        lowercasedProductName.includes('x-keeper online space')
      );
    });
  }

  public createLink(doc: 'epts' | 'lPPhoto' | 'appKDl' | 'appKDkp'): void {
    let file: Nullable<RequestFile> = null;

    switch (doc) {
      case 'epts':
        file = this.ePTS;
        break;
      case 'lPPhoto':
        file = this.lPPhoto;
        break;
      case 'appKDl':
        file = this.appKDl;
        break;
      case 'appKDkp':
        file = this.appKDkp;
        break;
      default:
        break;
    }

    if (!file) {
      this.xAlertService.error(
        this.translates[RequestsListItemTranslations.DocNotFound]!,
      );

      return;
    }

    this.openFile(file, doc === 'epts');
  }

  public openEpts(): void {
    this.createLink('epts');
  }

  private openFile(file: RequestFile, trackEptsLoading = false): void {
    if (isImageByFilename(file.filename)) {
      const previewData = this.getPreviewData(file);

      if (previewData) {
        this.$showPreviewImage.emit(previewData);

        return;
      }
    }

    if (file.filename.toLowerCase().endsWith('.pdf')) {
      if (trackEptsLoading) {
        if (this.$isOpeningEpts()) {
          return;
        }

        this.$isOpeningEpts.set(true);
        this.fileDownloadService
          .openPdfByPath$(file.path, file.filename)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => this.$isOpeningEpts.set(false)),
          )
          .subscribe();

        return;
      }

      this.fileDownloadService.openPdfByPath(file.path, file.filename);

      return;
    }

    this.fileDownloadService.downloadFile(
      environment.downloadFilesUrl + file.path,
      file.filename || file.path.split('/').pop() || file.path,
      false,
      this.renderer,
    );
  }

  private getPreviewData(
    file: RequestFile,
  ): [RequestFile, FileGroup, index: number] | null {
    const group = this.$mainFileGroups().find(currentGroup =>
      currentGroup.files.some(groupFile => groupFile.path === file.path),
    );

    if (!group) {
      return null;
    }

    const fileIndex = group.files.findIndex(
      groupFile => groupFile.path === file.path,
    );

    if (fileIndex < 0) {
      return null;
    }

    return [file, group, fileIndex];
  }

  public openAllWorkflowsLogsModal() {
    this.dialogService$
      .open(new PolymorpheusComponent(RequestWorkflowEventsDialogComponent), {
        closable: true,
        dismissible: true,
        size: null,
        data: {
          allWorkflows: true,
          requestNumber: this.$request().requestNumber,
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  // TODO TEMPORARY METHOD FOR OPENING WORKFLOWS DEBUGGING MODAL
  public openWorkflowsDebuggingModal() {
    this.dialogService$
      .open(new PolymorpheusComponent(WorkflowDebugComponent), {
        closable: true,
        dismissible: true,
        size: null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  // TODO TEMPORARY METHOD FOR OPENING WORKFLOWS SUBMISSIONS INFO MODAL
  public openWorkflowsSubmissionsInfoSheet() {
    this.dialogService$
      .open(new PolymorpheusComponent(WorkflowSubmissionsInfoComponent), {
        closable: true,
        dismissible: true,
        size: null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
